import React from 'react'
import { Field } from 'formik'
import { Columns, Heading, Element } from 'react-bulma-components'
import InputField from '../../form/fields/input'
import SelectField from '../../form/fields/select'
import InputFormulaField from '../../form/fields/input-formula-field'
import { BoundsConfigSectionProps } from './types'
import { getAvailablePathsForContext } from './input-formula-utils'

/**
 * Component that handles bounds configuration (min or max)
 * Manages fixed vs dynamic (simple path) vs formula (advanced AST) modes and conditional fields
 */
const BoundsConfigSection: React.FC<BoundsConfigSectionProps> = ({
  boundsType,
  values,
  availableEvents,
}) => {
  const isMin = boundsType === 'min'
  const boundsMode = isMin ? values.minBoundsMode : values.maxBoundsMode
  const modeFieldName = isMin ? 'interval.minBoundsMode' : 'interval.maxBoundsMode'
  const valueFieldName = isMin ? 'interval.min' : 'interval.max'
  const pathFieldName = isMin ? 'interval.minPath' : 'interval.maxPath'
  const formulaFieldName = isMin ? 'interval.minFormula' : 'interval.maxFormula'
  const fallbackFieldName = isMin ? 'interval.minFallback' : 'interval.maxFallback'
  const label = isMin ? 'Valeur minimale' : 'Valeur maximale'
  const valueLabel = isMin
    ? 'Règle appliquée à partir de (minutes)'
    : "Règle appliquée jusqu'à (minutes)"
  const pathLabel = isMin ? 'Chemin pour la valeur minimale' : 'Chemin pour la valeur maximale'
  const fallbackLabel = isMin
    ? 'Valeur de fallback pour min (minutes)'
    : 'Valeur de fallback pour max (minutes)'
  const pathHelp = 'Chemin vers une valeur dans les données de résumé'
  const fallbackHelp = "Valeur utilisée si le chemin retourne 0 ou n'existe pas"

  const period = values.period || ''
  const calcMode = values.mode
  const availablePaths = getAvailablePathsForContext(period, calcMode)

  return (
    <Columns>
      <Columns.Column size={12}>
        <Heading size={6}>{label}</Heading>
      </Columns.Column>
      <Columns.Column size={12}>
        <Field
          label="Mode de définition"
          name={modeFieldName}
          component={SelectField}
          options={
            calcMode === 'timeRange'
              ? [
                  { value: 'fixed', label: 'Valeur fixe' },
                  { value: 'dynamic', label: 'Valeur dynamique (chemin simple)' },
                ]
              : [
                  { value: 'fixed', label: 'Valeur fixe' },
                  { value: 'dynamic', label: 'Valeur dynamique (chemin simple)' },
                  { value: 'formula', label: 'Formule (avancé)' },
                ]
          }
          required
          fullwidth
        />
      </Columns.Column>
      <Columns.Column size={12}>
        {boundsMode === 'fixed' ? (
          <Field
            label={valueLabel}
            name={valueFieldName}
            component={InputField}
            type="number"
            required={isMin}
          />
        ) : boundsMode === 'dynamic' ? (
          <Field
            label={pathLabel}
            name={pathFieldName}
            component={SelectField}
            options={[
              { value: 'durations.contract.total', label: 'Durée totale du contrat' },
              {
                value: 'durations.contract.remainingAfterBenefit',
                label: 'Heures contractuelles restantes après prestations (résumé)',
              },
            ]}
            required
            help={pathHelp}
          />
        ) : (
          <Field
            label={
              isMin
                ? 'Formule pour la borne minimale (durée en ms côté serveur)'
                : 'Formule pour la borne maximale (durée en ms côté serveur)'
            }
            name={formulaFieldName}
            component={InputFormulaField}
            availablePaths={availablePaths}
            availableEvents={availableEvents}
            disabled={!period}
            required
            info={
              <>
                <Element renderAs="p" mb={2}>
                  <Element renderAs="strong">Mode formule :</Element> même principe que la formule
                  d&apos;input — composez des paths et des eventPath avec add/sub. Les références à
                  d&apos;autres règles ne sont pas autorisées dans les bornes.
                </Element>
                <Element renderAs="p" mb={2}>
                  Utilisez la même formule (ex. même eventPath) que pour l&apos;input si vous voulez
                  aligner la fenêtre [min, max] avec la base du calcul.
                </Element>
              </>
            }
          />
        )}
      </Columns.Column>
      {(boundsMode === 'dynamic' || boundsMode === 'formula') && (
        <Columns.Column size={6}>
          <Field
            label={fallbackLabel}
            name={fallbackFieldName}
            component={InputField}
            type="number"
            help={fallbackHelp}
          />
        </Columns.Column>
      )}
    </Columns>
  )
}

export default BoundsConfigSection
